import "./App.css";
import React, { Suspense, useEffect } from "react";
import ProtectedRoute from "./pages/authentication/ProtectedRoute";
import AppLayout from "./layout/AppLayout"; // Not lazy
import { Route, Routes, Navigate } from "react-router-dom";
import { BrowserRouter as Router } from "react-router-dom";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import ScrollToTop from "./components/scrolltop/ScrollToTop";
import { AuthProvider, useAuth } from "./pages/authentication/AuthContext";
import defaultTheme, {
  adminTheme,
  managerTheme,
  instructorTheme,
  studentTheme,
} from "./theme/theme";
import Busy from "./components/busy/Busy"; // Not lazy
import Error from "./pages/landingPage/404_page/Error";
import { useNavigate, useLocation } from "react-router-dom";
import PrivacyPolicy from "./pages/landingPage/policy/PrivacyPolicy"
import DataDeletionRequest from "./pages/landingPage/deleteaccount/DataDeletionRequest";
import TermsOfService from "./pages/landingPage/terms/TermsOfService";
import { getPrimaryBlendedColor } from "./utils/themeColor";

// Lazy Load Public Components
const HomeLayout = React.lazy(() => import("./pages/landingPage/homePage/HomeLayout"));
const Home = React.lazy(() => import("./pages/landingPage/home/Home"));
const About = React.lazy(() => import("./pages/landingPage/about/About"));
const CourseDetailsPage = React.lazy(() => import("./pages/landingPage/courses/CourseDetailsPage"));
const Features = React.lazy(() => import("./pages/landingPage/features/Features"));
const Team = React.lazy(() => import("./pages/landingPage/team/Team"));
const Testimonial = React.lazy(() => import("./pages/landingPage/testimonial/Testimonial"));
const Appointment = React.lazy(() => import("./pages/landingPage/appointment/Appointment"));
const Contact = React.lazy(() => import("./pages/landingPage/contact/Contact"));
const Login = React.lazy(() => import("./pages/authentication/Login"));
const Register = React.lazy(() => import("./pages/authentication/Register"));
const ResetPassword = React.lazy(() => import("./pages/authentication/ResetPassword"));
const Classroom = React.lazy(() => import("./components/onlineclass/Classroom"));

// Direct imports for protected components (no lazy loading needed inside AppLayout)

import {
  AdminDashboard,
  AdminCourses,
  AdminStaff,
  AdminDepartments,
  AdminBranches,
  AdminSchoolCharges,
  AdminBalanceSheet,
  AdminCashFlow,
  AdminIncomeStatement,
} from "./users/admin/adminexport";

import {
  CarManagement,
  Expenses,
  Profile,
  Students,
  Reports,
  Transactions,
  Settings,
  RefresherLessons,
  PracticalLessons,
  CourseView,
  DrivingCourses,
  ComputerCourses,
  RefresherCourses,
  StudentGuestCourses,
  Lessons,
  Notifications,
  StudentFilter,
} from "./users/common/commonexporter";

import {
  InstructorCourses,
  InstructorLessons,
  InstructorDashboard,
  CreateCourse,
  InstructorSupport,
  ScheduleStudents,
} from "./users/instructor/instructorexport";

import { ManagerDashboard } from "./users/manager/managerexport";
import ManagerSupport from "./users/manager/ManagerSupport";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PaymentResults from "./payment/PaymentResults";

import {
  StudentDashboard,
  StudentSupport,
} from "./users/student/studentexporter";
import ModelTownBoard from "./pages/student/ModelTownBoard";
import { SnackbarProvider } from "./contexts/SnackbarContext";

// Helper Component for Suspense Wrapping (only for public routes)
const LazyWrapper = ({ Component }) => (
  <Suspense fallback={<Busy />}>
    <Component />
  </Suspense>
);

// Route change debugger (remove in production)
const RouteChangeLogger = () => {
  const location = useLocation();
  
  React.useEffect(() => {
    console.log('Route changed to:', location.pathname);
  }, [location]);
  
  return null;
};

const roleThemes = {
  admin: adminTheme,
  manager: managerTheme,
  instructor: instructorTheme,
  student: studentTheme,
};

// Keeps <meta name="theme-color"> in sync with the active role theme so the
// Android/iOS status bar matches the AppBar exactly — no gap or thin line.
function ThemeColorSync() {
  const theme = useTheme();
  useEffect(() => {
    const color = getPrimaryBlendedColor(theme.palette.primary.main);
    // 1. Android PWA status bar color
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', color);
    // 2. Paint html + body the same color so nothing bleeds through
    //    behind the AppBar before it renders or during overscroll
    document.documentElement.style.backgroundColor = color;
    document.body.style.backgroundColor = color;
    // 3. Make sure #root itself also carries the color
    const root = document.getElementById('root');
    if (root) root.style.backgroundColor = color;
  }, [theme.palette.primary.main]);
  return null;
}

function ThemedContent() {
  const { user } = useAuth();
  const theme = roleThemes[user?.role] || defaultTheme;
  return (
    <ThemeProvider theme={theme}>
      <ThemeColorSync />
      <SnackbarProvider>
        <ScrollToTop />
        <RouteChangeLogger />
        <AppRoutes />
      </SnackbarProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Router>
        <AuthProvider>
          <ThemedContent />
        </AuthProvider>
      </Router>
    </LocalizationProvider>
  );
}

function LoginRedirect() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && user && location.pathname === "/") {
      navigate(`/${user.role}`, { replace: true });
    }
  }, [user, loading, location, navigate]);

  return null;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return <Busy />;
  }

  return (
    <>
      <LoginRedirect />
      <Routes>
        {/* Public Routes */}
        <Route path="/privacy_policy" element={<PrivacyPolicy />} />
        <Route path="/delete_data" element={<DataDeletionRequest />} />
        <Route path="/terms" element={<TermsOfService/>}/>
        <Route
          path="/"
          element={
            <Suspense fallback={<Busy />}>
              <HomeLayout />
            </Suspense>
          }
        >
          <Route index element={<LazyWrapper Component={Home} />} />
          <Route
            path="driving_courses"
            element={
              <LazyWrapper
                Component={() => <DrivingCourses course="Driving Courses" />}
              />
            }
          />
          <Route
            path="refresher_courses"
            element={
              <LazyWrapper
                Component={() => (
                  <RefresherCourses course="Refresher Courses" />
                )}
              />
            }
          />
          <Route
            path="course_details"
            element={<LazyWrapper Component={() => <CourseDetailsPage />} />}
          />
          <Route
            path="computer_it_courses"
            element={
              <LazyWrapper
                Component={() => <ComputerCourses title="Computer Courses" />}
              />
            }
          />
        
          <Route
            path="features"
            element={<LazyWrapper Component={Features} />}
          />
          <Route
            path="appointment"
            element={<LazyWrapper Component={Appointment} />}
          />
        
          <Route path="our-team" element={<LazyWrapper Component={Team} />} />
          <Route
            path="testimonial"
            element={<LazyWrapper Component={Testimonial} />}
          />
          <Route path="payment_results" element={<PaymentResults />} />
          <Route path="contact" element={<LazyWrapper Component={Contact} />} />
          <Route path="login" element={<LazyWrapper Component={Login} />} />
          <Route
            path="register"
            element={<LazyWrapper Component={Register} />}
          />
          <Route
            path="reset-password"
            element={<LazyWrapper Component={ResetPassword} />}
          />
          <Route path="about" element={<LazyWrapper Component={About} />} />
        </Route>

        {/* CONSOLIDATED Protected Routes - Single ProtectedRoute wrapper */}
        <Route element={<ProtectedRoute />}>
          {/* Admin Routes */}
          <Route path="/admin" element={<AppLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="departments" element={<AdminDepartments />} />
            <Route path="branches" element={<AdminBranches />} />
            <Route path="charges" element={<AdminSchoolCharges />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="staff" element={<AdminStaff />} />
            <Route path="students" element={<Students />} />
            <Route path="practical_lessons" element={<PracticalLessons />} />
            <Route path="refresher_lessons" element={<RefresherLessons />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="cashflow" element={<AdminCashFlow />} />
            <Route path="income_statement" element={<AdminIncomeStatement />} />
            <Route path="balance_sheet" element={<AdminBalanceSheet />} />
            <Route path="vehicles" element={<CarManagement />} />
            <Route path="settings" element={<Settings />} />
            <Route path="student_filter" element={<StudentFilter />} />
            <Route path="profile" element={<Profile />} />
            <Route path="reports" element={<Reports />} />
            <Route path="notifications" element={<Notifications />} />
          </Route>

          {/* Student Routes */}
          <Route path="/student" element={<AppLayout />}>
            <Route index element={<StudentDashboard />} />
            <Route path="lessons" element={<Lessons />} />
            <Route path="profile" element={<Profile />} />
            <Route
              path="online-class"
              element={
                <Suspense fallback={<Busy />}>
                  <Classroom />
                </Suspense>
              }
            />
            <Route path="my-courses" element={<StudentGuestCourses />} />
            <Route path="payments" element={<Transactions />} />
            <Route path="computer" element={<ComputerCourses />} />
            <Route path="driving" element={<DrivingCourses />} />
            <Route path="refresher" element={<RefresherCourses />} />
             <Route path="notifications" element={<Notifications />} />
            <Route
              path="course_details"
              element={
                <Suspense fallback={<Busy />}>
                  <CourseDetailsPage />
                </Suspense>
              }
            />
            <Route path="support" element={<StudentSupport />} />
            <Route path="model-town-board" element={<ModelTownBoard />} />
          </Route>

          {/* Instructor Routes */}
          <Route path="/instructor" element={<AppLayout />}>
            <Route index element={<InstructorDashboard />} />
            <Route path="create_course" element={<CreateCourse />} />
            <Route
              path="online-class"
              element={
                <Suspense fallback={<Busy />}>
                  <Classroom />
                </Suspense>
              }
            />
            <Route path="my-students" element={<Students />} />
            <Route path="my-courses" element={<InstructorCourses />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="mylessons" element={<InstructorLessons />} />
            <Route path="course_details" element={<CourseView />} />
            <Route path="schedule-students" element={<ScheduleStudents />} />
            <Route path="profile" element={<Profile />} />
            <Route path="support" element={<InstructorSupport />} />
             <Route path="notifications" element={<Notifications />} />
          </Route>

          {/* Manager Routes */}
          <Route path="/manager" element={<AppLayout />}>
            <Route index element={<ManagerDashboard />} />
            <Route path="students" element={<Students />} />
            <Route path="refresher_lessons" element={<RefresherLessons />} />
            <Route path="practical_lessons" element={<PracticalLessons />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="profile" element={<Profile />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="settings" element={<Settings />} />
            <Route path="support" element={<ManagerSupport />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="student_filter" element={<StudentFilter />} />
          </Route>
        </Route>

        {/* Catch-all Route */}
        <Route path="*" element={<Error />} />
      </Routes>
    </>
  );
}

export default App;